import { HTMLAttributes, ThHTMLAttributes, TdHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

// Base Table Container
const tableVariants = cva('min-w-full divide-y divide-border', {
  variants: {
    variant: {
      default: 'divide-y divide-border',
      admin: 'divide-y divide-border',
      simple: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tableContainerVariants = cva('overflow-x-auto', {
  variants: {
    variant: {
      default: 'rounded-lg border border-border',
      admin: '', // Admin tables usually wrapped in Card
      simple: 'rounded-lg border border-border',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Table Header variants
const tableHeaderVariants = cva('bg-muted/50', {
  variants: {
    variant: {
      default: 'bg-muted/50',
      admin: 'border-b border-border',
      simple: 'bg-muted/30',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Table Row variants
const tableRowVariants = cva('border-b border-border transition-colors', {
  variants: {
    variant: {
      default: 'hover:bg-muted/30',
      admin: 'hover:bg-muted/50',
      simple: 'hover:bg-muted/20',
    },
    state: {
      default: '',
      selected: 'bg-accent/50',
      loading: 'animate-pulse',
    },
  },
  defaultVariants: {
    variant: 'default',
    state: 'default',
  },
});

// Base interfaces
export interface TableProps
  extends HTMLAttributes<HTMLTableElement>,
    VariantProps<typeof tableVariants> {
  container?: boolean;
  containerClassName?: string;
}

export interface TableHeaderProps
  extends HTMLAttributes<HTMLTableSectionElement>,
    VariantProps<typeof tableHeaderVariants> {}

export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement>;

export interface TableRowProps
  extends HTMLAttributes<HTMLTableRowElement>,
    VariantProps<typeof tableRowVariants> {}

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
  sticky?: boolean;
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  sticky?: boolean;
}

// Table Container Component
export const TableContainer = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    variant?: 'default' | 'admin' | 'simple';
    className?: string;
  }
>(({ children, variant = 'default', className, ...props }, ref) => (
  <div ref={ref} className={cn(tableContainerVariants({ variant }), className)} {...props}>
    {children}
  </div>
));
TableContainer.displayName = 'TableContainer';

// Base Table Component
export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant, container = true, containerClassName, children, ...props }, ref) => {
    const table = (
      <table ref={ref} className={cn(tableVariants({ variant }), className)} {...props}>
        {children}
      </table>
    );

    if (container) {
      return (
        <TableContainer variant={variant || 'default'} className={containerClassName}>
          {table}
        </TableContainer>
      );
    }

    return table;
  }
);
Table.displayName = 'Table';

// Table Header Component
export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, variant, children, ...props }, ref) => (
    <thead ref={ref} className={cn(tableHeaderVariants({ variant }), className)} {...props}>
      {children}
    </thead>
  )
);
TableHeader.displayName = 'TableHeader';

// Table Body Component
export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, children, ...props }, ref) => (
    <tbody ref={ref} className={cn('bg-background divide-y divide-border', className)} {...props}>
      {children}
    </tbody>
  )
);
TableBody.displayName = 'TableBody';

// Table Row Component
export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, variant, state, children, ...props }, ref) => (
    <tr ref={ref} className={cn(tableRowVariants({ variant, state }), className)} {...props}>
      {children}
    </tr>
  )
);
TableRow.displayName = 'TableRow';

// Table Head Cell Component
export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  (
    {
      className,
      children,
      sortable = false,
      sortDirection = null,
      onSort,
      sticky = false,
      ...props
    },
    ref
  ) => {
    const content = sortable ? (
      <button
        onClick={onSort}
        className="flex items-center gap-1 hover:text-foreground transition-colors uppercase min-h-[44px] py-2 -my-2" // Larger touch target on mobile
      >
        {children}
        {sortDirection === 'asc' && <ChevronUpIcon className="h-4 w-4" />}
        {sortDirection === 'desc' && <ChevronDownIcon className="h-4 w-4" />}
      </button>
    ) : (
      <span className="uppercase">{children}</span>
    );

    return (
      <th
        ref={ref}
        className={cn(
          'px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sm:py-3', // Larger padding on mobile
          sticky && 'sticky left-0 bg-muted/50',
          className
        )}
        {...props}
      >
        {content}
      </th>
    );
  }
);
TableHead.displayName = 'TableHead';

// Table Cell Component
export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, sticky = false, children, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'px-4 py-5 whitespace-nowrap sm:py-4', // Larger padding on mobile for easier tapping
        sticky && 'sticky left-0 bg-background',
        className
      )}
      {...props}
    >
      {children}
    </td>
  )
);
TableCell.displayName = 'TableCell';

// Loading skeleton component for table rows
export const TableLoadingRow = ({
  columns,
  variant = 'default',
}: {
  columns: number;
  variant?: 'default' | 'admin' | 'simple';
}) => (
  <TableRow variant={variant} state="loading">
    {[...Array(columns)].map((_, i) => (
      <TableCell key={i}>
        <div className="h-4 bg-muted rounded animate-pulse" />
      </TableCell>
    ))}
  </TableRow>
);

// Empty state component
export const TableEmptyRow = ({
  columns,
  message = 'No data available',
  variant = 'default',
}: {
  columns: number;
  message?: string;
  variant?: 'default' | 'admin' | 'simple';
}) => (
  <TableRow variant={variant}>
    <TableCell colSpan={columns} className="text-center py-8">
      <span className="text-muted-foreground">{message}</span>
    </TableCell>
  </TableRow>
);
